import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { CopyIdButton, MobileSortControl } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import { formatDateTime, formatShortId } from '../format';
import type {
  BackofficeCommunicationResponse,
  SortDirection,
  UserInAppCommunicationSortBy,
} from '../types';

const SORTABLE_HEADERS: Record<UserInAppCommunicationSortBy, string> = {
  id: 'Message ID',
  templateKey: 'Template',
  status: 'Status',
  channelStatus: 'Channel status',
  createdAt: 'Created',
  deliveredAt: 'Delivered',
  readAt: 'Read',
};

const MOBILE_SORT_OPTIONS = Object.entries(SORTABLE_HEADERS).map(([value, label]) => ({
  value: value as UserInAppCommunicationSortBy,
  label,
}));

export interface UserInAppCommunicationsTableProps {
  communications: BackofficeCommunicationResponse[];
  sortBy: UserInAppCommunicationSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: UserInAppCommunicationSortBy) => void;
  onSelect: (communication: BackofficeCommunicationResponse) => void;
}

function getPayloadString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' ? value : null;
}

function SortButton({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  label: string;
  field: UserInAppCommunicationSortBy;
  sortBy: UserInAppCommunicationSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: UserInAppCommunicationSortBy) => void;
}) {
  const active = field === sortBy;
  const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className="focus-ring inline-flex items-center gap-1 rounded-md text-left font-semibold text-neutral-600"
      aria-label={`Sort by ${label}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export function UserInAppCommunicationsTable({
  communications,
  sortBy,
  sortDirection,
  onSortChange,
  onSelect,
}: UserInAppCommunicationsTableProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div>
        <MobileSortControl
          label="Sort in-app communications"
          options={MOBILE_SORT_OPTIONS}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <div className="divide-y divide-neutral-100">
          {communications.map((communication) => (
            <article
              key={communication.id}
              role="button"
              tabIndex={0}
              aria-label={`Open in-app message ${formatShortId(communication.id)}`}
              className="cursor-pointer px-4 py-4 hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
              onClick={() => onSelect(communication)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(communication);
                }
              }}
            >
              <div className="text-body font-semibold text-neutral-900">
                {getPayloadString(communication.inApp?.displayPayload ?? {}, 'title') ??
                  communication.templateKey}
              </div>
              <div className="mt-1 text-small text-neutral-600">
                {communication.inApp?.channelSpecificStatus ?? communication.status} | Delivered{' '}
                {formatDateTime(communication.inApp?.deliveredAt ?? null)}
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-[1120px] w-full border-collapse text-left"
        aria-label="In-app communications"
      >
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="min-w-[150px] px-4 py-3">
              <SortButton
                label="Message ID"
                field="id"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[240px] px-4 py-3 font-semibold text-neutral-600">
              Title
            </th>
            {Object.entries(SORTABLE_HEADERS)
              .filter(([field]) => field !== 'id')
              .map(([field, label]) => (
                <th key={field} scope="col" className="min-w-[150px] px-4 py-3">
                  <SortButton
                    label={label}
                    field={field as UserInAppCommunicationSortBy}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSortChange={onSortChange}
                  />
                </th>
              ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {communications.map((communication) => (
            <tr
              key={communication.id}
              tabIndex={0}
              aria-label={`Open in-app message ${formatShortId(communication.id)}`}
              className="cursor-pointer hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
              onClick={() => onSelect(communication)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(communication);
                }
              }}
            >
              <td className="px-4 py-4">
                <div className="group inline-flex items-center gap-2">
                  <span className="font-medium tabular-nums text-neutral-700">
                    {formatShortId(communication.id)}
                  </span>
                  <CopyIdButton value={communication.id} label="Copy message ID" />
                </div>
              </td>
              <td className="px-4 py-4 text-neutral-900">
                {getPayloadString(communication.inApp?.displayPayload ?? {}, 'title') ?? 'No title'}
              </td>
              <td className="px-4 py-4 text-neutral-700">{communication.templateKey}</td>
              <td className="px-4 py-4 text-neutral-700">{communication.status}</td>
              <td className="px-4 py-4 text-neutral-700">
                {communication.inApp?.channelSpecificStatus ?? 'Not available'}
              </td>
              <td className="px-4 py-4 text-neutral-700">
                {formatDateTime(communication.createdAt)}
              </td>
              <td className="px-4 py-4 text-neutral-700">
                {formatDateTime(communication.inApp?.deliveredAt ?? null)}
              </td>
              <td className="px-4 py-4 text-neutral-700">
                {formatDateTime(communication.inApp?.readAt ?? null)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
